import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useAtomValue } from "jotai";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Icon, Spinner } from "@common/index";
import { Input } from "@common/Form/Input";
import { useAcceptUi, type AcceptUiCapture } from "@/hooks/useAcceptUi";
import { useAcceptJs, type AcceptJsBankCapture } from "@/hooks/useAcceptJs";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useBankAccountApi } from "@/hooks/apiHooks/useBankAccountApi";
import { organizationsAtom } from "@/store/organizationAtoms";
import { PaymentMethodType } from "@models/paymentMethod";
import type { BankAccountResponse } from "@models/bankAccount";
import {
    CHECKOUT_UNAVAILABLE_MESSAGE,
    CHECKOUT_UNAVAILABLE_TITLE,
} from "../_helpers/checkoutUnavailableCopy";

// The payment-method choice surface (#296): Card (AcceptUI hosted lightbox, unchanged) or
// Bank account/ACH (saved-account picker + hand-rolled form tokenized via Accept.js). Raw
// payment details never touch our state on either branch: the card lives inside the vendor
// iframe, and the bank fields transit react-hook-form state and the Accept.js call only —
// after tokenization we hold just the opaque nonce plus display fragments (masked card /
// account last4).

// What checkout sends for the selected payment. Exactly one of the three shapes — a saved
// bank profile is the API's reusable Guid id, the other two carry a single-use vendor
// nonce. The union is what guarantees a request never carries both credentials (the
// server 400s that combination).
export type PaymentSelection =
    | { method: PaymentMethodType.Card; vendorPaymentToken: string }
    | { method: PaymentMethodType.BankAccount; vendorPaymentToken: string }
    | { method: PaymentMethodType.BankAccount; paymentProfileId: string };

// Imperative surface the Checkout page drives on "Place Order". getPaymentSelection resolves
// the stored selection, and NEVER resolves without one (#429): it throws when payment is not
// configured (no public client key — there is nothing to pay with, and an order without a
// payment is never placed), and throws with an actionable message when the selected method
// has nothing captured/picked yet. resetPayment discards consumed nonces after a failed order
// attempt — see its comment for what deliberately survives.
export interface PaymentFormHandle {
    getPaymentSelection: () => Promise<PaymentSelection>;
    resetPayment: () => void;
}

export interface PaymentFormProps {
    // Fired on every method toggle. Phase B's surcharge line hangs off this — the fee must
    // track the chosen method BEFORE Place Order (§1.11).
    onMethodChange?: (method: PaymentMethodType) => void;
    // True while a bank capture is in flight with the vendor. Checkout gates Place Order on
    // it: the ACH branch is the only path with an async capture window OUTSIDE the submit
    // sequence (the card lightbox completes before control returns here), and clicking Place
    // Order inside that window used to fail with "enter your bank details" at the exact
    // moment the shopper had just entered them.
    onCaptureInFlightChange?: (inFlight: boolean) => void;
}

const CARD_MISSING_MESSAGE =
    "Enter your payment details before placing the order.";
const ACH_MISSING_MESSAGE =
    "Choose a saved bank account or enter your bank details before placing the order.";
// Soft note, not an error: the saved-account read may fail in an environment
// deployed before the endpoint landed (PR 242 — see useBankAccountApi) — ACH must
// stay usable via the add-new form regardless.
const SAVED_ACCOUNT_CHECK_FAILED =
    "We couldn't check for a saved bank account.";

// Vendor account-type values (Accept.js casing) are the form's option values, so the submit
// payload needs no mapping table.
const bankAccountSchema = z
    .object({
        routingNumber: z
            .string()
            .regex(/^\d{9}$/, "Enter the 9-digit routing number"),
        accountNumber: z
            .string()
            .regex(/^\d{4,17}$/, "Enter an account number of 4 to 17 digits"),
        confirmAccountNumber: z.string(),
        nameOnAccount: z.string().trim().min(1, "Name on account is required"),
        accountType: z.enum(["checking", "savings", "businessChecking"]),
    })
    .refine((data) => data.accountNumber === data.confirmAccountNumber, {
        path: ["confirmAccountNumber"],
        message: "Account numbers must match",
    });

type BankAccountFormData = z.infer<typeof bankAccountSchema>;

const EMPTY_BANK_FORM: BankAccountFormData = {
    routingNumber: "",
    accountNumber: "",
    confirmAccountNumber: "",
    nameOnAccount: "",
    accountType: "checking",
};

// idle = ACH never selected yet (fetch is lazy — most checkouts never leave the card
// branch). degraded = the saved-account check failed: ACH continues via the add-new
// form with a soft note (never a blocking error — see SAVED_ACCOUNT_CHECK_FAILED).
type AccountsLoadState = "idle" | "loading" | "degraded" | "ready";

export const PaymentForm = forwardRef<PaymentFormHandle, PaymentFormProps>(
    ({ onMethodChange, onCaptureInFlightChange }, ref) => {
        const { getMeOrganizations } = useOrganizationApi();
        const { getActiveBankAccount } = useBankAccountApi();
        const knownOrganizations = useAtomValue(organizationsAtom);

        const [method, setMethod] = useState(PaymentMethodType.Card);
        const [capture, setCapture] = useState<AcceptUiCapture | null>(null);
        const [error, setError] = useState<string | null>(null);

        // ACH branch state. Mutual exclusion is by construction: a saved pick only happens in
        // the picker (no capture can exist there), and a capture is only minted from the form
        // (reached by clearing the pick) — so getPaymentSelection never has to arbitrate.
        const [accountsLoadState, setAccountsLoadState] =
            useState<AccountsLoadState>("idle");
        const [savedAccounts, setSavedAccounts] = useState<
            BankAccountResponse[]
        >([]);
        const [selectedSavedId, setSelectedSavedId] = useState<string | null>(
            null,
        );
        const [showAddForm, setShowAddForm] = useState(false);
        const [bankCapture, setBankCapture] =
            useState<AcceptJsBankCapture | null>(null);
        const [isTokenizing, setIsTokenizing] = useState(false);

        const { isConfigured, openLightbox } = useAcceptUi({
            onCapture: (next) => {
                setCapture(next);
                setError(null);
            },
            onError: (message) => setError(message),
        });

        const acceptJs = useAcceptJs();
        // The toggle exists only when BOTH vendor scripts can work; with keys but no
        // VITE_AUTHNET_ACCEPT_URL the surface stays card-only, exactly as before #296.
        const showMethodToggle = isConfigured && acceptJs.isConfigured;

        const methods = useForm<BankAccountFormData>({
            resolver: zodResolver(bankAccountSchema),
            defaultValues: EMPTY_BANK_FORM,
        });

        // The full account number must not outlive the vendor call on ANY exit path — success,
        // vendor rejection, or walking away to the card branch. PaymentForm stays mounted for
        // the whole checkout, so without this a rejected number would sit in form state (and in
        // a cleartext DOM input) for the rest of the session, in reach of session-replay and
        // error-reporting tools. Routing/name/type deliberately survive: they are not the
        // secret, and an Edit round-trip should only re-ask for what we dropped.
        const { resetField } = methods;
        const clearAccountNumbers = useCallback(() => {
            resetField("accountNumber");
            resetField("confirmAccountNumber");
        }, [resetField]);

        // One setter so the local disable and Checkout's Place Order gate can never disagree
        // about whether a capture is in flight.
        const setCaptureInFlight = useCallback(
            (inFlight: boolean) => {
                setIsTokenizing(inFlight);
                onCaptureInFlightChange?.(inFlight);
            },
            [onCaptureInFlightChange],
        );

        useImperativeHandle(
            ref,
            () => ({
                getPaymentSelection: async () => {
                    setError(null);

                    // No public client key — nothing can be captured, so there is no
                    // selection to hand back (#429). Checkout never gets here: its handler
                    // returns first on the same predicate (isCardPaymentConfigured). This
                    // stays because it is the handle's own contract — it never resolves
                    // without an instrument, whoever calls it — and it names the real
                    // reason; without it an unconfigured call would fall through to the
                    // card branch's misleading "Enter your payment details".
                    if (!isConfigured) {
                        throw new Error(CHECKOUT_UNAVAILABLE_MESSAGE);
                    }

                    if (method === PaymentMethodType.Card) {
                        if (!capture) {
                            setError(CARD_MISSING_MESSAGE);
                            throw new Error(CARD_MISSING_MESSAGE);
                        }
                        return {
                            method: PaymentMethodType.Card,
                            vendorPaymentToken: capture.token,
                        };
                    }

                    if (selectedSavedId) {
                        return {
                            method: PaymentMethodType.BankAccount,
                            paymentProfileId: selectedSavedId,
                        };
                    }
                    if (bankCapture) {
                        return {
                            method: PaymentMethodType.BankAccount,
                            vendorPaymentToken: bankCapture.token,
                        };
                    }
                    setError(ACH_MISSING_MESSAGE);
                    throw new Error(ACH_MISSING_MESSAGE);
                },
                resetPayment: () => {
                    // Nonces are single-use whichever vendor script minted them, so BOTH
                    // captures are discarded. A paymentProfileId is NOT a nonce — it is the
                    // API's reusable Guid — so the saved pick (and the selected method)
                    // survive a failed attempt: the shopper retries without re-picking.
                    setCapture(null);
                    setBankCapture(null);
                    setError(null);
                },
            }),
            [isConfigured, method, capture, selectedSavedId, bankCapture],
        );

        const loadSavedAccounts = async () => {
            setAccountsLoadState("loading");
            try {
                // Same organization source as the app's payment wall
                // (AddPaymentMethodPanel): a user belongs to exactly one organization and
                // /organizations/me returns an array. Prefer the value the app has already
                // loaded — this is a lazy side-lookup, and re-fetching cost a second serial
                // round trip before the bank read could even start.
                const organizations =
                    knownOrganizations.length > 0
                        ? knownOrganizations
                        : await getMeOrganizations({ silent: true });
                const organizationId = organizations[0]?.id;
                // A checked-out customer ALWAYS belongs to an organization, so no id here is
                // an anomaly (empty/partial response), not a shopper with nothing saved.
                // Treat it as degraded: same working add-new form, but the soft note tells
                // them the check didn't happen — otherwise someone whose org does have a
                // saved profile silently re-keys their routing and account numbers.
                if (!organizationId) {
                    setSavedAccounts([]);
                    setAccountsLoadState("degraded");
                    return;
                }
                // Team B's model: at most ONE active bank profile per org (null/404 =
                // none). Kept in the existing array state so the picker code — a
                // legitimate radio group even for one option — stays untouched.
                const account = await getActiveBankAccount(organizationId);
                setSavedAccounts(account ? [account] : []);
                setAccountsLoadState("ready");
            } catch {
                // A failed check must not dead-end ACH (e.g. an environment deployed
                // before PR 242) — degrade to the add-new form with a soft note
                // instead of a blocking error + retry.
                setSavedAccounts([]);
                setAccountsLoadState("degraded");
            }
        };

        const handleMethodChange = (next: PaymentMethodType) => {
            if (next === method) return;
            setMethod(next);
            // A message about the other method is stale the moment the toggle flips.
            setError(null);
            // Leaving the bank form drops any number still sitting in it — the shopper who
            // gives up on ACH after a rejection must not carry their account number into the
            // rest of the session on the card branch.
            clearAccountNumbers();
            onMethodChange?.(next);
            if (
                next === PaymentMethodType.BankAccount &&
                accountsLoadState === "idle"
            ) {
                void loadSavedAccounts();
            }
        };

        const onSubmitBankAccount = async (data: BankAccountFormData) => {
            setError(null);
            setCaptureInFlight(true);
            try {
                const nextCapture = await acceptJs.tokenizeBankAccount({
                    accountNumber: data.accountNumber,
                    routingNumber: data.routingNumber,
                    nameOnAccount: data.nameOnAccount,
                    accountType: data.accountType,
                });
                setBankCapture(nextCapture);
            } catch (failure) {
                // tokenizeBankAccount always rejects with customer-safe copy (vendor message
                // or its own generic fallback).
                setError(
                    failure instanceof Error
                        ? failure.message
                        : "Your bank details could not be verified. Please check them and try again.",
                );
            } finally {
                // Both exits clear the numbers — a rejected account number is exactly as
                // sensitive as an accepted one, and re-typing it is the cost of not keeping it.
                clearAccountNumbers();
                setCaptureInFlight(false);
            }
        };

        // Deliberately does NOT clear `capture`: cancelling an edit (or a vendor error on re-open)
        // must never destroy an already-valid saved card. Only a successful capture replaces it, and
        // only resetPayment (after a failed order attempt) discards it.
        const handleOpen = () => {
            setError(null);
            openLightbox();
        };

        const methodToggle = (
            <fieldset>
                <legend className="sr-only">Payment method</legend>
                <div className="flex flex-wrap gap-2">
                    {(
                        [
                            [PaymentMethodType.Card, "credit_card", "Card"],
                            [
                                PaymentMethodType.BankAccount,
                                "account_balance",
                                "Bank account (ACH)",
                            ],
                        ] as const
                    ).map(([value, icon, label]) => (
                        <label
                            key={value}
                            // has-[:focus-visible]: keyboard focus sits on the sr-only
                            // radio inside, so the LABEL must show the focus ring or
                            // keyboard users can't see which toggle has focus.
                            className={`btn btn-sm has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 ${
                                method === value
                                    ? "btn-neutral"
                                    : "btn-outline text-neutral border-neutral hover:bg-base-200"
                            }`}
                        >
                            <input
                                type="radio"
                                name="checkout-payment-method"
                                className="sr-only"
                                checked={method === value}
                                onChange={() => handleMethodChange(value)}
                            />
                            <Icon name={icon} className="text-xl" />
                            {label}
                        </label>
                    ))}
                </div>
            </fieldset>
        );

        const cardBranch = capture ? (
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-neutral">
                    <Icon name="credit_card" className="text-xl" />
                    <span className="font-semibold">
                        {capture.maskedCard || "Card captured"}
                    </span>
                    {capture.expDate && (
                        <span className="text-neutral/60">
                            Expires {capture.expDate}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleOpen}
                    className="btn btn-sm btn-outline text-neutral border-neutral hover:bg-base-200"
                >
                    Edit
                </button>
            </div>
        ) : (
            <button
                type="button"
                onClick={handleOpen}
                className="btn btn-outline w-full md:w-auto md:self-start text-neutral border-neutral hover:bg-base-200"
            >
                <Icon name="credit_card" className="text-xl" />
                Enter payment details
            </button>
        );

        const bankAddForm = (
            <FormProvider {...methods}>
                <form
                    onSubmit={methods.handleSubmit(onSubmitBankAccount)}
                    className="flex flex-col gap-3"
                >
                    {/* autoComplete="off" on all four: these are the only fields in the app
                        that carry bank credentials, and checkout runs on shared B2B and kiosk
                        workstations where browser form-history or a password manager would
                        otherwise offer one shopper's account number to the next. inputMode
                        (not type="number") gets the digit keypad without stripping the leading
                        zeros that routing numbers legitimately start with. */}
                    <Input
                        name="routingNumber"
                        placeholder="Routing number"
                        inputClass="text-black"
                        autoComplete="off"
                        inputMode="numeric"
                    />
                    <Input
                        name="accountNumber"
                        placeholder="Account number"
                        inputClass="text-black"
                        autoComplete="off"
                        inputMode="numeric"
                    />
                    <Input
                        name="confirmAccountNumber"
                        placeholder="Confirm account number"
                        inputClass="text-black"
                        autoComplete="off"
                        inputMode="numeric"
                    />
                    <Input
                        name="nameOnAccount"
                        placeholder="Name on account"
                        inputClass="text-black"
                        autoComplete="off"
                    />
                    {/* A select with the vendor's option values. The other fields name
                        themselves through their placeholder, so this one carries an
                        aria-label (same pattern as the state select in LocationFormModal —
                        @common/Form/Select has no accessible-name path besides a visible
                        label). */}
                    <select
                        aria-label="Account type"
                        className="select select-bordered w-full bg-white text-neutral"
                        {...methods.register("accountType")}
                    >
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                        <option value="businessChecking">
                            Business checking
                        </option>
                    </select>
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="submit"
                            disabled={isTokenizing}
                            className="btn btn-outline text-neutral border-neutral hover:bg-base-200"
                        >
                            <Icon name="account_balance" className="text-xl" />
                            {isTokenizing
                                ? "Verifying bank details…"
                                : "Use this bank account"}
                        </button>
                        {savedAccounts.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setShowAddForm(false)}
                                className="btn btn-ghost btn-sm text-neutral"
                            >
                                Back to saved accounts
                            </button>
                        )}
                    </div>
                </form>
            </FormProvider>
        );

        const bankPicker = (
            <div className="flex flex-col gap-3">
                <fieldset className="flex flex-col gap-2">
                    <legend className="sr-only">Saved bank accounts</legend>
                    {savedAccounts.map((account) => (
                        <label
                            key={account.paymentProfileId}
                            className="flex items-center gap-3 text-sm text-neutral cursor-pointer"
                        >
                            <input
                                type="radio"
                                name="checkout-saved-bank-account"
                                className="radio radio-sm"
                                checked={
                                    selectedSavedId ===
                                    account.paymentProfileId
                                }
                                onChange={() =>
                                    setSelectedSavedId(
                                        account.paymentProfileId,
                                    )
                                }
                            />
                            {/* last4 is the ONLY display field the confirmed contract
                                carries (PR 242) — and it is nullable, so never render
                                a "••••null" mask. */}
                            <span className="font-semibold">
                                {account.accountLast4
                                    ? `Bank account ••••${account.accountLast4}`
                                    : "Bank account"}
                            </span>
                        </label>
                    ))}
                </fieldset>
                <button
                    type="button"
                    onClick={() => {
                        // Entering the form leaves the picker: clear the pick so the
                        // eventual capture is unambiguous in getPaymentSelection.
                        setSelectedSavedId(null);
                        setShowAddForm(true);
                    }}
                    className="btn btn-ghost btn-sm self-start text-neutral"
                >
                    Use a different bank account
                </button>
            </div>
        );

        const bankCaptureDisplay = bankCapture && (
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-neutral">
                    <Icon name="account_balance" className="text-xl" />
                    <span className="font-semibold">
                        {`Bank account ••••${bankCapture.accountLast4}`}
                    </span>
                </div>
                {/* Unlike the card lightbox there is no cancel gesture to protect, and the
                    number fields were already cleared — so Edit simply discards the (free to
                    re-mint) nonce and reopens the form. */}
                <button
                    type="button"
                    onClick={() => setBankCapture(null)}
                    className="btn btn-sm btn-outline text-neutral border-neutral hover:bg-base-200"
                >
                    Edit
                </button>
            </div>
        );

        const bankBranch =
            accountsLoadState === "loading" ? (
                <div role="status" className="flex justify-center py-4">
                    <Spinner size="md" />
                    <span className="sr-only">
                        Loading saved bank accounts
                    </span>
                </div>
            ) : bankCapture ? (
                bankCaptureDisplay
            ) : showAddForm || savedAccounts.length === 0 ? (
                <>
                    {accountsLoadState === "degraded" && (
                        <p className="text-sm text-neutral/70">
                            {SAVED_ACCOUNT_CHECK_FAILED}
                        </p>
                    )}
                    {bankAddForm}
                </>
            ) : (
                bankPicker
            );

        return (
            <div className="bg-base-100 rounded-lg p-4 md:p-5">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-lg text-neutral">Payment</h2>
                </div>

                <div className="flex flex-col gap-4">
                    {/* Explicit text-error-content: the light theme sets
                        --color-base-content to white (see the CLAUDE.md theme
                        quirk), so the alert must not inherit it. */}
                    {!isConfigured ? (
                        <div
                            role="alert"
                            data-testid="checkout-unavailable"
                            className="alert alert-error text-error-content"
                        >
                            <Icon name="error" filled />
                            <div>
                                <h3 className="font-bold">
                                    {CHECKOUT_UNAVAILABLE_TITLE}
                                </h3>
                                <p>{CHECKOUT_UNAVAILABLE_MESSAGE}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {showMethodToggle && methodToggle}
                            {method === PaymentMethodType.Card
                                ? cardBranch
                                : bankBranch}
                        </>
                    )}

                    {error && (
                        <p role="alert" className="text-sm text-error">
                            {error}
                        </p>
                    )}

                    <p className="flex items-center gap-1 text-xs text-neutral/60">
                        <Icon name="lock" className="text-sm" />
                        All transactions are encrypted and processed securely.
                    </p>
                </div>
            </div>
        );
    },
);

PaymentForm.displayName = "PaymentForm";
